import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { queryClient } from '../components/queryClient';
import { useHasPermission } from '../hooks/useHasPermission';
import { BulkEventEditJobsListResponse } from '../interfaces';
import { BulkEventEditJobService } from '../services';

const QUERY_KEY = 'bulk-event-edit-jobs';

export const useBulkEventEditJobsQuery = (limit: number, offset: number, statusFilter?: string) => {
  const canView = useHasPermission('bulk_event_edit_jobs:read:list');

  const queryResult = useQuery<BulkEventEditJobsListResponse>({
    queryKey: [QUERY_KEY, limit, offset, statusFilter],
    queryFn: () => BulkEventEditJobService.listJobs(limit, offset, statusFilter),
    enabled: canView,
  });

  // Accumulate all pages loaded so far for the current filter
  const allJobs = useMemo(() => {
    const jobs: BulkEventEditJobsListResponse['jobs'] = [];
    const pages = Math.floor(offset / limit) + 1;
    for (let page = 0; page < pages; page++) {
      const pageOffset = page * limit;
      const cached = queryClient.getQueryData<BulkEventEditJobsListResponse>([QUERY_KEY, limit, pageOffset, statusFilter]);
      if (cached?.jobs) {
        jobs.push(...cached.jobs);
      }
    }

    return jobs;
  }, [limit, offset, statusFilter, queryResult.data]);

  return { ...queryResult, allJobs };
};

export const forceRefreshBulkEventEditJobs = () => {
  queryClient.invalidateQueries([QUERY_KEY]);
};
